import { Models } from '../../pipeline/models/global.models';

/**
 * Returns the display label for the notes/scratchpad dropdown option.
 *
 * When LoanNotesV2 is enabled, the label changes based on pipeline type
 * (e.g. "Loan Notes" for loans). When disabled or for unsupported types,
 * falls back to "Scratchpad".
 *
 * Accepts `string` to support both general and pipeline PipelineType enums.
 */
export function getNotesLabel(pipelineType: string | undefined, isV2Enabled: boolean): string {
  if (!isV2Enabled || !pipelineType) {
    return Models.QuickActionType.Scratchpad;
  }
  switch (pipelineType) {
    case Models.PipelineType.Loan:
      return Models.QuickActionType.LoanNotes;

    // Future cases: Models.PipelineType.Lead, Models.PipelineType.LoanApplication, etc.

    default:
      return Models.QuickActionType.Scratchpad;
  }
}

/**
 * Sorts items by their position in a predefined name-order array.
 * Items not in the order array sort to the end, alphabetically among themselves.
 *
 * @param items Array to sort (not mutated — returns a new array)
 * @param order Display order by name (e.g., LOAN_NOTE_TAG_ORDER)
 * @param nameKey Property to use for name lookup (default: 'name')
 */
export function sortByTagOrder<T>(items: T[], order: string[], nameKey: keyof T = 'name' as keyof T): T[] {
  if (!items?.length) {
    return items;
  }
  return [...items].sort((a, b) => {
    const nameA = String(a[nameKey]);
    const nameB = String(b[nameKey]);
    const posA = order.indexOf(nameA);
    const posB = order.indexOf(nameB);
    const idxA = posA === -1 ? order.length : posA;
    const idxB = posB === -1 ? order.length : posB;
    if (idxA !== idxB) {
      return idxA - idxB;
    }
    return nameA.localeCompare(nameB);
  });
}

/**
 * Strips HTML tags from content to get plain text for searching.
 * Inserts spaces before block elements to prevent text from different
 * paragraphs/lines from concatenating (e.g., "dfa" + "asdf" → "dfa asdf", not "dfaasdf").
 */
export function stripHtml(html: string): string {
  // Replace &nbsp; with regular spaces so search matches multi-word phrases
  const normalized = html.replace(/&nbsp;/gi, ' ');
  // Insert spaces before block elements to prevent text concatenation across boundaries
  const spacedHtml = normalized.replace(/<(p|div|br|li|h[1-6])/gi, ' <$1');
  const tmp = document.createElement('div');
  tmp.innerHTML = spacedHtml;
  const text = tmp.textContent || tmp.innerText || '';
  // Normalize multiple spaces to single space
  return text.replace(/\s+/g, ' ').trim();
}

/**
 * Strips HTML tags but preserves line breaks for plain text output.
 * Used for legacy note saving where V1 API expects plain text but should retain formatting.
 */
export function stripHtmlPreserveNewlines(html: string): string {
  // Replace &nbsp; with regular spaces
  let text = html.replace(/&nbsp;/gi, ' ');
  // Replace closing block tags with newlines to preserve paragraph structure
  text = text.replace(/<\/p>/gi, '\n');
  text = text.replace(/<br\s*\/?>/gi, '\n');
  text = text.replace(/<\/div>/gi, '\n');
  text = text.replace(/<\/li>/gi, '\n');
  // Strip all remaining HTML tags
  text = text.replace(/<[^>]+>/g, '');
  // Normalize multiple consecutive newlines to max 2 (one blank line)
  text = text.replace(/\n{3,}/g, '\n\n');
  // Trim trailing spaces on each line but preserve newlines
  text = text
    .split('\n')
    .map((line) => line.trim())
    .join('\n');
  // Trim leading/trailing whitespace from entire text
  return text.trim();
}
